import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Eye, Calendar, User, Tag } from "lucide-react";
import SuccesModal from "../Sections/SuccesModal";
import {
  delete_blog,
  get_blog,
  server_post_data,
} from "../../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import DeleteModal from "../Sections/DeleteModal";

export default function Blog() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showPreview, setShowPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const itemsPerPage = 8;

  // Delete Handler
  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const fd = new FormData();
      fd.append("primary_id", deleteId);
      const response = await server_post_data(delete_blog, fd);
      console.log("Delete Response:", response.data);
      setDeleteUserModal(false);
      setShowSuccessModal(true);
      master_get_data(); // Refresh list
    } catch (error) {
      console.error("Delete Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = React.useMemo(() => {
    if (!search.trim()) return blogs;
  
    const searchLower = search.toLowerCase();
  
    return blogs.filter((blog) => {
      const title = String(blog.title || "").toLowerCase();
      const subtitle = String(blog.subtitle || "").toLowerCase();
      const shortDesc = String(blog.shortDesc || "").toLowerCase();
      const category = String(blog.category || "").toLowerCase();
      const author = String(blog.author || "").toLowerCase();
  
      return (
        title.includes(searchLower) ||
        subtitle.includes(searchLower) ||
        shortDesc.includes(searchLower) ||
        category.includes(searchLower) ||
        author.includes(searchLower)
      );
    });
  }, [blogs, search]);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    master_get_data();
  }, []);

  const master_get_data = async () => {
    try {
      setIsLoading(true);
      const fd = new FormData();
      const response = await server_post_data(get_blog, fd);
      const data = response.data?.data || [];
      setBlogs(data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogs([]); // Ensure blogs is always an array
    } finally {
      setIsLoading(false);
    }
  };

  const hasBlogs = blogs.length > 0;
  const hasResults = filteredData.length > 0;

  return (
    <MasterLayout>
      {isLoading && <Loader />}

      <div className="mx-auto bg-white p-6 rounded-lg shadow border">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">All Blogs</h2>
              <p className="text-gray-500 text-sm">Manage your blog posts easily</p>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search blogs..."
                className="border rounded-lg px-4 py-3 w-full sm:w-96 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                onClick={() => navigate("/add_blog")}
                className="px-6 py-3 bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] text-white rounded-lg font-medium shadow transition"
              >
                + Add Blog
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sr. No.</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title & Subtitle</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Short Description</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Author</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!hasResults ? (
                  <tr>
                    <td colSpan="8" className="text-center py-16 text-gray-500">
                      <div className="text-6xl mb-4 opacity-50">📝</div>
                      <p className="text-2xl font-medium">
                        {hasBlogs
                          ? "No blogs match your search"
                          : "No blogs found"}
                      </p>
                      <p className="text-sm mt-2">
                        {hasBlogs
                          ? "Try adjusting your search term"
                          : "Start by adding your first blog!"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((blog, index) => (
                    <tr key={blog.primary_id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <img
                          src={blog.blogImgFull || "/placeholder-image.jpg"}
                          alt="blog"
                          className="w-20 h-14 object-cover rounded-lg border shadow-sm bg-gray-200"
                        />
                      </td>

                      <td className="px-6 py-4 max-w-xs">
                        <p className="font-bold text-gray-900 text-base line-clamp-2">
                          {blog.title || "No Title"}
                        </p>
                        <p className="text-[var(--primary-color)] font-medium text-sm mt-1 line-clamp-3">
                          {blog.subtitle || "No Subtitle"}
                        </p>
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md">
                        <p className="line-clamp-3">{blog.shortDesc || "No description"}</p>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {String(blog.category || "")
                            .split(",")
                            .map((cat) => cat.trim())
                            .filter(Boolean)
                            .map((cat, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-[var(--primary-color)]"
                              >
                                {cat}
                              </span>
                            ))}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                        {blog.author || "Unknown"}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {blog.created_at
                          ? new Date(blog.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center items-center gap-3">
                          <button
                            onClick={() => setShowPreview(blog)}
                            className="p-2.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                            title="Preview"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/edit_blog/${blog.primary_id}`)}
                            className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteId(blog.primary_id);
                              setDeleteUserModal(true);
                            }}
                            className="p-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Only show if there are results and multiple pages */}
          {hasResults && totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} blogs
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-hover)]"
                  }`}
                >
                  Previous
                </button>
                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-10 h-10 rounded-lg font-medium transition ${
                        currentPage === i + 1
                          ? "bg-[var(--primary-color)] text-white"
                          : "bg-white border hover:bg-gray-100"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    currentPage === totalPages
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-hover)]"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900">Blog Preview</h2>
              <button
                onClick={() => setShowPreview(null)}
                className="text-gray-500 hover:text-gray-800 text-3xl font-light hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              {showPreview.blogImgFull && (
                <img
                  src={showPreview.blogImgFull}
                  alt={showPreview.title}
                  className="w-full h-96 object-cover rounded-xl shadow-lg mb-8"
                />
              )}

              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Title</label>
                <h1 className="text-3xl font-bold text-gray-900 mt-2">{showPreview.title || "No Title"}</h1>
              </div>

              {showPreview.subtitle && (
                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Subtitle</label>
                  <p className="text-xl font-medium text-[var(--primary-color)] mt-2">{showPreview.subtitle}</p>
                </div>
              )}

              {showPreview.shortDesc && (
                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Short Description</label>
                  <p className="text-gray-700 mt-2 leading-relaxed">{showPreview.shortDesc}</p>
                </div>
              )}

              <div className="mb-8">
                <label className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Full Description</label>
                <div
                  className="prose prose-lg max-w-none mt-4 text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: showPreview.fullDesc || "No content available." }}
                />
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600 border-t pt-6">
                <span className="flex items-center gap-2">
                  <User size={18} /> {showPreview.author || "Unknown"}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={18} />
                  {showPreview.created_at
                    ? new Date(showPreview.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })
                    : "N/A"}
                </span>
                <span className="flex items-center gap-2">
                  <Tag size={18} /> Categories:
                  <div className="flex flex-wrap gap-2 ml-2">
                    {String(showPreview.category || "")
                      .split(",")
                      .map((c) => c.trim())
                      .filter(Boolean)
                      .map((cat, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-100 text-[var(--primary-color)] rounded-full text-xs">
                          {cat}
                        </span>
                      ))}
                  </div>
                </span>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 rounded-b-2xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowPreview(null)}
                  className="px-8 py-3 bg-[var(--primary-color)] text-white rounded-lg hover:bg-[var(--primary-hover)] transition font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <DeleteModal
        open={deleteUserModal}
        onClose={() => setDeleteUserModal(false)}
        onConfirm={handleDelete}
        title="Delete this Blog?"
        subtitle="This action cannot be undone. The blog will be permanently removed."
      />

      <SuccesModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Blog Deleted!"
        subtitle="The blog has been successfully removed."
        autoClose={true}
        autoCloseTime={3000}
      />
    </MasterLayout>
  );
}